import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

const defaultMeta = {
    siteName: 'Borrowed',
    description: 'Find what you need, lend what you don\'t use. The ultimate platform for sustainable sharing and borrowing.',
    type: 'website',
    image: '/logo512.png',
    twitterHandle: '@getborrowed'
};

export default function SEOProvider({
    title,
    description,
    type = 'website',
    image,
    noIndex = false,
    children
}) {
    const location = useLocation();
    const canonicalUrl = `https://getborrowed.com${location.pathname}`;

    const metaDescription = description || defaultMeta.description;
    const metaImage = `https://getborrowed.com${image || defaultMeta.image}`;
    const pageTitle = title ? `${title} | ${defaultMeta.siteName}` : defaultMeta.siteName;

    return (
        <>
            <Helmet>
                {/* Basic Meta Tags */}
                <title>{pageTitle}</title>
                <meta name="description" content={metaDescription} />
                <link rel="canonical" href={canonicalUrl} />
                {noIndex && <meta name="robots" content="noindex,nofollow" />}

                {/* Open Graph */}
                <meta property="og:site_name" content={defaultMeta.siteName} />
                <meta property="og:title" content={pageTitle} />
                <meta property="og:description" content={metaDescription} />
                <meta property="og:type" content={type} />
                <meta property="og:url" content={canonicalUrl} />
                <meta property="og:image" content={metaImage} />

                {/* Twitter Card */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:site" content={defaultMeta.twitterHandle} />
                <meta name="twitter:title" content={pageTitle} />
                <meta name="twitter:description" content={metaDescription} />
                <meta name="twitter:image" content={metaImage} />

                {/* JSON-LD structured data */}
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "WebSite",
                        "name": defaultMeta.siteName,
                        "url": "https://getborrowed.com",
                        "potentialAction": {
                            "@type": "SearchAction",
                            "target": "https://getborrowed.com/listings?search={search_term_string}",
                            "query-input": "required name=search_term_string"
                        }
                    })}
                </script>
            </Helmet>
            {children}
        </>
    );
}
